import { useEffect, useMemo, useState } from 'react';
import {
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock3,
  Database,
  FileText,
  FolderOpen,
  Info,
  LoaderCircle,
  LockKeyhole,
  Plus,
  RotateCw,
  Search,
  Server,
  ShieldCheck,
  Sparkles,
  TriangleAlert,
} from 'lucide-react';
import type {
  ContentCentralBindingProposal,
  ContentCentralCatalogDefinition,
  ContentCentralCatalogSummary,
  ContentCentralFilingPreviewResult,
  ContentCentralMappingChoice,
  ContentCentralProvisioningPlan,
  DestinationConnectionSummary,
  JobDestinationBinding,
} from '../shared/desktop-api';
import { connectionProblem, connectionStateLine } from './ConnectionsPanel';
import { OperatorErrorText, TechnicalDetails } from './TechnicalDetails';
import { WorkspaceDialog } from './WorkspaceDialog';
import './content-central-setup.css';

export type CcField = { id: string; name: string; kind: string; required: boolean };
export type CcDocumentType = { id: string; name: string; status: string; fields: CcField[] };
export type CcJob = { id: string; name: string; documentTypes: CcDocumentType[] };

/** Paige's own reminder (not Content Central's words) that a saved matching must be checked again. */
export const REVIEW_BEFORE_RESUMING = 'Review this mapping before Content Central export resumes.';

export const sameContentCentralId = (left?: string | null, right?: string | null) =>
  Boolean(left && right && left.toLowerCase() === right.toLowerCase());

const countOf = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/** Names that read the same, ignoring case and spacing: how an operator would see a same-name match. */
const sameName = (left?: string | null, right?: string | null) => {
  const plain = (value?: string | null) => (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  return plain(left).length > 0 && plain(left) === plain(right);
};

export function mappingChoicesFromProposal(
  proposal: ContentCentralBindingProposal,
  job: CcJob,
  previous: ContentCentralMappingChoice[] = [],
): ContentCentralMappingChoice[] {
  const result: ContentCentralMappingChoice[] = [];
  for (const localType of job.documentTypes.filter((item) => item.status !== 'archived')) {
    const binding = proposal.binding?.documentTypes.find(
      (item) => item.localDocumentTypeId === localType.id,
    );
    const decision = proposal.mappings?.find(
      (item) => item.objectKind === 'document-type' && item.localId === localType.id,
    );
    const prior = previous.find((item) => item.localDocumentTypeId === localType.id);
    const remoteDocumentTypeId =
      decision?.remoteId ?? prior?.remoteDocumentTypeId ?? binding?.remoteDocumentTypeId;
    if (!remoteDocumentTypeId) continue;
    const fields: Record<string, string | null> = {};
    if (binding) {
      for (const field of localType.fields.filter(
        (item) => item.kind !== 'document-type' && item.kind !== 'line-items',
      )) {
        const mapped = binding.fields.find(
          (item) => item.localFieldId === field.id && !item.isLineItem,
        );
        if (mapped) fields[field.id] = mapped.deliverValue ? mapped.remoteFieldId : null;
      }
    }
    Object.assign(fields, prior?.fields ?? {});
    for (const field of proposal.mappings?.filter(
      (item) => item.objectKind === 'field' && item.parentLocalDocumentTypeId === localType.id,
    ) ?? []) {
      if (field.remoteId) fields[field.localId] = field.remoteId;
      else if (field.classification === 'operator-selected') fields[field.localId] = null;
    }
    result.push({ localDocumentTypeId: localType.id, remoteDocumentTypeId, fields });
  }
  return result;
}

export function contentCentralMappingChoiceMatches(
  left?: ContentCentralMappingChoice,
  right?: ContentCentralMappingChoice,
): boolean {
  if (
    !left ||
    !right ||
    !sameContentCentralId(left.remoteDocumentTypeId, right.remoteDocumentTypeId)
  )
    return left === right;
  const localFieldIds = new Set([...Object.keys(left.fields), ...Object.keys(right.fields)]);
  for (const fieldId of localFieldIds) {
    // "Do not send" (null, or no choice) matches only itself; two remote fields match by id.
    const a = left.fields[fieldId] ?? null,
      b = right.fields[fieldId] ?? null;
    if (a === null || b === null ? a !== b : !sameContentCentralId(a, b)) return false;
  }
  return true;
}

/** A filing example as one line of path pieces: folder › folder › file. */
export function FilingPath({
  folders,
  fileName,
  label,
}: {
  folders: string[];
  fileName: string;
  label?: string;
}) {
  return (
    <div className="cc-path">
      {label && <span className="cc-path-type">{label}</span>}
      <span className="cc-path-pieces">
        {folders.map((folder, index) => (
          <span key={index} className="cc-path-folder">
            <FolderOpen size={14} aria-hidden="true" />
            {folder}
            <i aria-hidden="true">›</i>
          </span>
        ))}
        <span className="cc-path-file">
          <FileText size={14} aria-hidden="true" />
          <b>{fileName}</b>
        </span>
      </span>
    </div>
  );
}

function StateLine({ connection }: { connection: DestinationConnectionSummary }) {
  return <small>{connectionStateLine(connection)}</small>;
}

export function ContentCentralSteps({ step }: { step: 'connect' | 'catalog' | 'match' }) {
  const order = ['connect', 'catalog', 'match'] as const;
  const at = order.indexOf(step);
  return (
    <ol className="cc-steps" aria-label="Content Central setup steps">
      {order.map((item, index) => (
        <li
          key={item}
          className={index < at ? 'done' : index === at ? 'current' : ''}
          aria-current={index === at ? 'step' : undefined}
        >
          <span className="cc-step-mark">{index < at ? <Check size={11} /> : index + 1}</span>
          {item === 'connect' ? 'Connect' : item === 'catalog' ? 'Catalog' : 'Match'}
        </li>
      ))}
    </ol>
  );
}

/** Step 1: which saved connection this job uses. */
export function ContentCentralConnectStep({
  connections,
  selectedId,
  disabled,
  onChoose,
  onOpenConnections,
}: {
  connections: DestinationConnectionSummary[];
  selectedId: string;
  disabled: boolean;
  onChoose: (connectionId: string) => void;
  onOpenConnections: () => void;
}) {
  return (
    <div className="cc-connect">
      <span className="cc-label" id="cc-connect-label">
        Which Content Central should this job use?
      </span>
      {connections.length === 0 ? (
        <div className="cc-empty">
          <Database size={20} aria-hidden="true" />
          <span>
            <strong>No Content Central connection yet</strong>
            <small>Add one once on this computer, then any job can use it.</small>
          </span>
        </div>
      ) : (
        <div className="cc-choice-list" role="list" aria-labelledby="cc-connect-label">
          {connections.map((connection) => {
            const failing = Boolean(connection.lastFailureCode);
            return (
              <button
                key={connection.connectionId}
                type="button"
                role="listitem"
                disabled={disabled}
                className={`cc-choice${failing ? ' attention' : ''}${sameContentCentralId(connection.connectionId, selectedId) ? ' selected' : ''}`}
                onClick={() => (failing ? onOpenConnections() : onChoose(connection.connectionId))}
                aria-label={`${connection.displayName}, ${connectionStateLine(connection)}${failing ? '. Opens Connections' : ''}`}
              >
                <span className="cc-icon">
                  <Server size={18} aria-hidden="true" />
                </span>
                <span className="cc-choice-text">
                  <strong>{connection.displayName}</strong>
                  {failing ? (
                    <small>{connectionProblem(connection)} · open Connections to fix it</small>
                  ) : (
                    <StateLine connection={connection} />
                  )}
                </span>
                <span
                  className={`cc-pill${failing ? ' attention' : connection.lastSuccessAt ? ' ready' : ''}`}
                >
                  {failing ? (
                    <>
                      <TriangleAlert size={12} aria-hidden="true" />
                      Needs attention
                    </>
                  ) : connection.lastSuccessAt ? (
                    <>
                      <Check size={12} aria-hidden="true" />
                      Ready
                    </>
                  ) : (
                    'Not checked yet'
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
      <div className="cc-add-connection">
        <button
          type="button"
          className="quiet-button small"
          disabled={disabled}
          onClick={onOpenConnections}
        >
          <Plus size={14} /> Add a connection
        </button>
        <small>Opens This computer › Connections, shared by every job.</small>
      </div>
    </div>
  );
}

/** Step 2: find a catalog, or create one from this job. */
export function ContentCentralCatalogStep({
  connection,
  justAdded,
  onChangeConnection,
  query,
  onQuery,
  catalogs,
  loading,
  loaded,
  error,
  onRetry,
  moreError,
  onRefreshMore,
  canShowMore,
  moreLoading,
  onShowMore,
  limitReached,
  renderLimit,
  onChoose,
  onCreate,
  creating,
  locked,
  current = null,
  onKeepCurrent,
  createError = null,
}: {
  /** Why the last Create a new catalog attempt failed, with its Technical details. */
  createError?: string | null;
  connection: DestinationConnectionSummary | null;
  justAdded: boolean;
  /** The catalog this job uses now, when the operator came here to change it. */
  current?: { id: string; name: string } | null;
  onKeepCurrent?: () => void;
  onChangeConnection: () => void;
  query: string;
  onQuery: (value: string) => void;
  catalogs: ContentCentralCatalogSummary[];
  loading: boolean;
  loaded: boolean;
  error: string;
  onRetry: () => void;
  moreError: string;
  onRefreshMore: () => void;
  canShowMore: boolean;
  moreLoading: boolean;
  onShowMore: () => void;
  limitReached: boolean;
  renderLimit: number;
  onChoose: (catalogId: string) => void;
  onCreate: () => void;
  creating: boolean;
  locked: boolean;
}) {
  return (
    <div className="cc-catalog">
      {connection &&
        (justAdded ? (
          <div className="cc-added" role="status">
            <Check size={14} aria-hidden="true" />
            <span>
              <strong>{connection.displayName} connected</strong> and chosen for this job. Pick a
              catalog next.
            </span>
            <button
              type="button"
              className="quiet-button small"
              disabled={locked}
              onClick={onChangeConnection}
            >
              Change
            </button>
          </div>
        ) : (
          <div className="cc-row">
            <span className="cc-icon small">
              <Server size={15} aria-hidden="true" />
            </span>
            <strong>{connection.displayName}</strong>
            <span className="cc-pill ready">
              <Check size={12} aria-hidden="true" />
              Ready
            </span>
            <button
              type="button"
              className="quiet-button small cc-row-action"
              disabled={locked}
              onClick={onChangeConnection}
            >
              Change
            </button>
          </div>
        ))}
      {current && onKeepCurrent && (
        <div className="cc-row">
          <span className="cc-icon small">
            <Database size={15} aria-hidden="true" />
          </span>
          <span className="cc-choice-text">
            <strong>{current.name}</strong>
            <small>This job’s catalog now · pick another below</small>
          </span>
          <button
            type="button"
            className="secondary-button small cc-row-action"
            disabled={locked}
            onClick={onKeepCurrent}
          >
            Keep this catalog
          </button>
        </div>
      )}
      <label className="cc-search">
        <Search size={15} aria-hidden="true" />
        <input
          value={query}
          disabled={locked}
          onChange={(event) => onQuery(event.target.value)}
          placeholder="Find a catalog"
          aria-label="Find a catalog"
        />
      </label>
      {error ? (
        <div className="cc-inline-error" role="alert" data-telemetry="error:catalog-load-failed">
          <TriangleAlert size={14} aria-hidden="true" />
          <span>
            <strong>Catalogs could not be loaded</strong>
            <OperatorErrorText message={error} />
          </span>
          <button
            type="button"
            className="secondary-button small"
            disabled={locked}
            onClick={onRetry}
          >
            Try again
          </button>
        </div>
      ) : (
        <div
          className="cc-choice-list cc-catalog-list"
          role="list"
          aria-label="Content Central catalogs"
          aria-busy={loading}
        >
          {loading && catalogs.length === 0 && (
            <p className="cc-state" role="status">
              <LoaderCircle className="spin" size={14} /> Finding catalogs…
            </p>
          )}
          {catalogs.map((catalog) =>
            catalog.canAddDocuments ? (
              <button
                key={catalog.id}
                type="button"
                role="listitem"
                className={`cc-choice${current && sameContentCentralId(catalog.id, current.id) ? ' selected' : ''}`}
                disabled={locked}
                onClick={() =>
                  current && onKeepCurrent && sameContentCentralId(catalog.id, current.id)
                    ? onKeepCurrent()
                    : onChoose(catalog.id)
                }
              >
                <span className="cc-icon small">
                  <Database size={15} aria-hidden="true" />
                </span>
                <span className="cc-choice-text">
                  <strong>{catalog.name}</strong>
                  <small>
                    {catalog.documentTypeCount} document{' '}
                    {catalog.documentTypeCount === 1 ? 'type' : 'types'}
                  </small>
                </span>
                {current && sameContentCentralId(catalog.id, current.id) ? (
                  <span className="cc-pill ready">
                    <Check size={12} aria-hidden="true" />
                    Current
                  </span>
                ) : (
                  <ChevronRight size={16} aria-hidden="true" />
                )}
              </button>
            ) : (
              <div
                key={catalog.id}
                role="listitem"
                className="cc-choice view-only"
                title="Can't add documents here"
                aria-disabled="true"
              >
                <span className="cc-icon small">
                  <Database size={15} aria-hidden="true" />
                </span>
                <span className="cc-choice-text">
                  <strong>{catalog.name}</strong>
                  <small>
                    {catalog.documentTypeCount} document{' '}
                    {catalog.documentTypeCount === 1 ? 'type' : 'types'}
                  </small>
                </span>
                <span className="cc-pill">View only</span>
              </div>
            ),
          )}
          {loaded && !loading && catalogs.length === 0 && query.trim() && (
            <p className="cc-state" role="status">
              No catalog matches “{query.trim()}”.{' '}
              <button type="button" className="quiet-button small" onClick={() => onQuery('')}>
                Clear search
              </button>
            </p>
          )}
          {/* Content Central lists only the catalogs this connection may use, so an
            empty list says nothing about the server itself. */}
          {loaded && !loading && catalogs.length === 0 && !query.trim() && (
            <p className="cc-state">
              This connection can’t see any catalogs yet. Give it access in Content Central, or
              create one from this job.
            </p>
          )}
        </div>
      )}
      {moreError && (
        <div className="cc-inline-error" role="alert" data-telemetry="error:catalog-load-failed">
          <TriangleAlert size={13} aria-hidden="true" />
          <span>
            <strong>More catalogs couldn’t be loaded</strong>
            <OperatorErrorText message={moreError} />
          </span>
          <button
            type="button"
            className="quiet-button small"
            disabled={locked || moreLoading}
            onClick={onRefreshMore}
          >
            Refresh results
          </button>
        </div>
      )}
      {!moreError && canShowMore && (
        <div className="cc-more">
          <small>{catalogs.length.toLocaleString()} shown</small>
          <button
            type="button"
            className="secondary-button small"
            disabled={locked || moreLoading}
            onClick={onShowMore}
          >
            {moreLoading ? <LoaderCircle className="spin" size={13} /> : <ChevronDown size={13} />}
            {moreLoading ? 'Loading…' : 'Show more'}
          </button>
        </div>
      )}
      {limitReached && (
        <small className="cc-note">
          Search by name to find catalogs beyond these {renderLimit.toLocaleString()} results.
        </small>
      )}
      {createError && !creating && (
        <div className="cc-inline-error" role="alert" data-telemetry="error:catalog-plan-failed">
          <TriangleAlert size={14} aria-hidden="true" />
          <span>
            <strong>Content Central couldn’t plan the new catalog</strong>
            <OperatorErrorText message={createError} />
          </span>
          <button
            type="button"
            className="secondary-button small"
            disabled={locked || Boolean(error) || !loaded}
            onClick={onCreate}
          >
            Try again
          </button>
        </div>
      )}
      <button
        type="button"
        className="cc-create"
        disabled={locked || creating || Boolean(error) || !loaded}
        onClick={onCreate}
      >
        <span className="cc-icon spark">
          {creating ? (
            <LoaderCircle className="spin" size={16} />
          ) : (
            <Sparkles size={16} aria-hidden="true" />
          )}
        </span>
        <span className="cc-choice-text">
          <strong>
            {creating ? 'Preparing a new catalog…' : 'Create a new catalog from this job'}
          </strong>
          <small>Content Central builds the types, fields and filing to match</small>
        </span>
        <ChevronRight size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

export type ContentCentralWaiting =
  | {
      kind: 'types';
      types: Array<{
        name: string;
        plural: string;
        remoteName: string;
        count: number | null;
        reason: string;
      }>;
    }
  | {
      kind: 'paused';
      title: string;
      detail?: string | null;
      action: 'connections' | 'matching' | 'connection-choice';
      note: string;
    };

/** Step 3 once matched: the connection, the catalog, and examples Content Central builds. */
export function ContentCentralSummary({
  connection,
  catalogName,
  matchText,
  matchAttention,
  waiting,
  unmatched = [],
  locked,
  onChangeConnection,
  onChangeCatalog,
  onOpenMatching,
  onOpenConnections,
  showExamples,
  examples,
  examplesLoading,
  examplesError,
  onRetryExamples,
  pendingDecision,
  onOpenPending,
}: {
  showExamples: boolean;
  connection: DestinationConnectionSummary | null;
  catalogName: string;
  matchText: string;
  matchAttention: boolean;
  waiting: ContentCentralWaiting | null;
  /** This job's document types with no Content Central type in the saved matching, and how many documents of each wait. */
  unmatched?: Array<{ name: string; plural: string; count: number | null }>;
  locked: boolean;
  onChangeConnection: () => void;
  onChangeCatalog: () => void;
  onOpenMatching: () => void;
  onOpenConnections: () => void;
  examples: Array<{ typeName: string; folders: string[]; fileName: string }>;
  examplesLoading: boolean;
  examplesError: string;
  onRetryExamples: () => void;
  pendingDecision: string | null;
  onOpenPending: () => void;
}) {
  return (
    <div className="cc-summary">
      {pendingDecision && (
        <div className="cc-warning" role="status">
          <Clock3 size={16} aria-hidden="true" />
          <span className="cc-warning-text">
            <strong>{pendingDecision}</strong>
          </span>
          <button type="button" className="secondary-button small" onClick={onOpenPending}>
            Open
          </button>
        </div>
      )}
      {waiting?.kind === 'types' && (
        <div
          className="cc-warning"
          role="alert"
          data-telemetry="error:content-central-types-changed"
        >
          <TriangleAlert size={16} aria-hidden="true" />
          <span className="cc-warning-text">
            {waiting.types.length === 1 ? (
              <>
                <strong>
                  {waiting.types[0].count
                    ? `${waiting.types[0].count} ${waiting.types[0].count === 1 ? waiting.types[0].name.toLowerCase() + ' is' : waiting.types[0].plural.toLowerCase() + ' are'} waiting.`
                    : `${waiting.types[0].plural} are waiting.`}
                </strong>{' '}
                Content Central changed its {waiting.types[0].remoteName} type. Review how{' '}
                {waiting.types[0].plural.toLowerCase()} match; every other document type keeps
                exporting.
              </>
            ) : (
              <>
                <strong>
                  {waiting.types.length} document types are waiting:{' '}
                  {waiting.types.map((item) => item.name).join(', ')}.
                </strong>{' '}
                Content Central changed them. Review how they match; every other document type keeps
                exporting.
              </>
            )}
            <TechnicalDetails
              detail={waiting.types.map((item) => `${item.name}: ${item.reason}`).join('\n')}
            />
          </span>
          <button
            type="button"
            className="secondary-button small"
            disabled={locked}
            onClick={onOpenMatching}
          >
            Review matching
          </button>
        </div>
      )}
      {unmatched.length > 0 && (
        <div className="cc-warning" role="status">
          <TriangleAlert size={16} aria-hidden="true" />
          <span className="cc-warning-text">
            {unmatched.length === 1 ? (
              <>
                <strong>
                  {unmatched[0].count
                    ? `${unmatched[0].count.toLocaleString()} ${unmatched[0].count === 1 ? unmatched[0].name.toLowerCase() + ' waits' : unmatched[0].plural.toLowerCase() + ' wait'} here.`
                    : `${unmatched[0].plural} wait here.`}
                </strong>{' '}
                {unmatched[0].name} isn’t matched to a Content Central document type yet. Match it
                to send them; nothing is lost.
              </>
            ) : (
              <>
                <strong>
                  {unmatched.length} document types wait here:{' '}
                  {unmatched
                    .map((item) =>
                      item.count ? `${item.name} (${item.count.toLocaleString()})` : item.name,
                    )
                    .join(', ')}
                  .
                </strong>{' '}
                They aren’t matched to Content Central document types yet. Match them to send their
                documents; nothing is lost.
              </>
            )}
          </span>
          <button
            type="button"
            className="secondary-button small"
            disabled={locked}
            onClick={onOpenMatching}
          >
            Match
          </button>
        </div>
      )}
      {waiting?.kind === 'paused' && (
        <div
          className="cc-warning stop"
          role="alert"
          data-telemetry={
            waiting.action === 'connections'
              ? 'error:content-central-failed'
              : 'error:content-central-paused'
          }
        >
          <TriangleAlert size={16} aria-hidden="true" />
          <span className="cc-warning-text">
            <strong>{waiting.title}</strong> {waiting.note}
            <TechnicalDetails detail={waiting.detail} />
          </span>
          <button
            type="button"
            className="secondary-button small"
            disabled={locked}
            onClick={
              waiting.action === 'matching'
                ? onOpenMatching
                : waiting.action === 'connection-choice'
                  ? onChangeConnection
                  : onOpenConnections
            }
          >
            {waiting.action === 'matching'
              ? 'Review matching'
              : waiting.action === 'connection-choice'
                ? 'Choose a connection'
                : 'Open Connections'}
          </button>
        </div>
      )}
      {connection && (
        <div className="cc-row">
          <span className="cc-icon small">
            <Server size={15} aria-hidden="true" />
          </span>
          <span className="cc-choice-text">
            <strong>{connection.displayName}</strong>
            <StateLine connection={connection} />
          </span>
          <button
            type="button"
            className="quiet-button small cc-row-action"
            disabled={locked}
            onClick={onChangeConnection}
          >
            Change
          </button>
        </div>
      )}
      <div className="cc-row">
        <span className="cc-icon small">
          <Database size={15} aria-hidden="true" />
        </span>
        <span className="cc-choice-text">
          <strong>{catalogName}</strong>
          <small className={matchAttention ? 'attention' : ''}>{matchText}</small>
        </span>
        <button
          type="button"
          className="quiet-button small cc-row-action"
          disabled={locked}
          onClick={onChangeCatalog}
          aria-label="Change catalog"
        >
          Change
        </button>
        <button
          type="button"
          className="secondary-button small"
          disabled={locked}
          onClick={onOpenMatching}
        >
          Change matching
        </button>
      </div>
      {showExamples && (
        <>
          <hr />
          <span className="cc-label">
            Examples built by Content Central · sample values, in the {catalogName} catalog
          </span>
          {examplesLoading && (
            <p className="cc-state" role="status">
              <LoaderCircle className="spin" size={14} /> Asking Content Central for examples…
            </p>
          )}
          {examplesError && (
            <div
              className="cc-inline-error"
              role="alert"
              data-telemetry="error:filing-examples-failed"
            >
              <TriangleAlert size={14} aria-hidden="true" />
              <span>
                <strong>Content Central couldn’t build the examples</strong>
                <OperatorErrorText message={examplesError} />
              </span>
              <button type="button" className="secondary-button small" onClick={onRetryExamples}>
                Try again
              </button>
            </div>
          )}
          <div className="cc-paths">
            {examples.map((example, index) => (
              <FilingPath
                key={`${index}:${example.typeName}`}
                label={example.typeName}
                folders={example.folders}
                fileName={example.fileName}
              />
            ))}
          </div>
          {waiting?.kind !== 'paused' && (
            <small className="cc-note cc-summary-foot">
              Content Central decides folders and file names for this catalog. Change them there.
            </small>
          )}
        </>
      )}
    </div>
  );
}

type FieldTag = { text: string; tone: 'exact' | 'chosen' | 'skip' | 'need' };

/**
 * Match to Content Central: this job's document types on the left, the chosen
 * type's Content Central type and fields in the middle, and Content Central's
 * own verdict on the right. Only same-name matches are filled in for the
 * operator; Check asks the engine, which reports the first problem it finds.
 */
export function ContentCentralMatchWindow({
  job,
  definition,
  proposal,
  choices,
  dirty,
  locked,
  reviewing,
  plan,
  applyState,
  outcomeKnown,
  applyError,
  applying,
  discarding,
  directPreview,
  holdReasons,
  initialTypeId,
  connectionName,
  onChoicesChange,
  onCheck,
  onUse,
  onKeep,
  onClose,
  checkError = null,
}: {
  /** Why the last Check with Content Central failed, with its Technical details. */
  checkError?: string | null;
  job: CcJob;
  definition: ContentCentralCatalogDefinition;
  proposal: ContentCentralBindingProposal | null;
  choices: ContentCentralMappingChoice[];
  dirty: boolean;
  locked: boolean;
  reviewing: boolean;
  plan: ContentCentralProvisioningPlan | null;
  applyState: string | null;
  outcomeKnown: boolean;
  applyError: string | null;
  applying: boolean;
  discarding: boolean;
  directPreview: ContentCentralFilingPreviewResult | null;
  holdReasons: Record<string, string>;
  initialTypeId?: string | null;
  connectionName: string;
  onChoicesChange: (choices: ContentCentralMappingChoice[]) => void;
  onCheck: () => void;
  onUse: () => void;
  onKeep: () => void;
  onClose: () => void;
}) {
  const localTypes = useMemo(
    () => job.documentTypes.filter((type) => type.status !== 'archived'),
    [job.documentTypes],
  );
  const [typeId, setTypeId] = useState(() =>
    initialTypeId && localTypes.some((type) => type.id === initialTypeId)
      ? initialTypeId
      : (localTypes[0]?.id ?? ''),
  );
  const [query, setQuery] = useState('');
  const reviewed = useMemo(
    () =>
      new Map(
        (proposal ? mappingChoicesFromProposal(proposal, job) : []).map((choice) => [
          choice.localDocumentTypeId,
          choice,
        ]),
      ),
    [job, proposal],
  );
  const accepted = Boolean(plan) && !dirty && (plan?.blockers.length ?? 0) === 0 && !reviewing;
  const inProgress = Boolean(applyState);

  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const describe = (localType: CcDocumentType) => {
    const mapped = proposal?.binding?.documentTypes.find(
      (item) => item.localDocumentTypeId === localType.id,
    );
    const decision = proposal?.mappings?.find(
      (item) => item.objectKind === 'document-type' && item.localId === localType.id,
    );
    const choice = choices.find((item) => item.localDocumentTypeId === localType.id);
    const proposedTypeId = decision?.remoteId ?? mapped?.remoteDocumentTypeId ?? '';
    const selectedTypeId = choice?.remoteDocumentTypeId ?? proposedTypeId;
    const typeChoiceChanged = Boolean(
      choice && !sameContentCentralId(choice.remoteDocumentTypeId, proposedTypeId),
    );
    const remote = definition.documentTypes.find((item) =>
      sameContentCentralId(item.id, selectedTypeId),
    );
    const fieldDecisions =
      proposal?.mappings?.filter(
        (item) => item.objectKind === 'field' && item.parentLocalDocumentTypeId === localType.id,
      ) ?? [];
    const fields = localType.fields
      .filter((field) => field.kind !== 'document-type' && field.kind !== 'line-items')
      .map((field) => {
        const fieldDecision = fieldDecisions.find((item) => item.localId === field.id);
        const choiceOwnsField = Boolean(
          choice && Object.prototype.hasOwnProperty.call(choice.fields, field.id),
        );
        const selected = choiceOwnsField
          ? (choice?.fields[field.id] ?? '')
          : typeChoiceChanged
            ? '__unmapped__'
            : (fieldDecision?.remoteId ??
              mapped?.fields.find((item) => item.localFieldId === field.id)?.remoteFieldId ??
              '__unmapped__');
        const remoteField = remote?.fields.find((item) => sameContentCentralId(item.id, selected));
        const tag: FieldTag =
          selected === ''
            ? { text: 'Not sent', tone: 'skip' }
            : !remoteField
              ? { text: 'Needs a choice', tone: 'need' }
              : (fieldDecision?.classification === 'exact' &&
                    sameContentCentralId(fieldDecision.remoteId, selected)) ||
                  sameName(field.name, remoteField.name)
                ? { text: 'Same name', tone: 'exact' }
                : { text: 'Your choice', tone: 'chosen' };
        return { field, selected: remoteField?.id ?? selected, tag };
      });
    const typeTag: FieldTag = !remote
      ? { text: 'Needs a choice', tone: 'need' }
      : (decision?.classification === 'exact' && !typeChoiceChanged) ||
          sameName(localType.name, remote.name)
        ? { text: 'Same name', tone: 'exact' }
        : { text: 'Your choice', tone: 'chosen' };
    const needs = !remote || fields.some((item) => item.tag.tone === 'need');
    const changedSinceReview =
      dirty && !contentCentralMappingChoiceMatches(choice, reviewed.get(localType.id));
    const planType = plan?.changes.find(
      (item) =>
        item.objectKind === 'document-type' &&
        (sameContentCentralId(item.remoteId, mapped?.remoteDocumentTypeId) ||
          item.displayName === localType.name),
    );
    const proposedFiling = sameContentCentralId(selectedTypeId, mapped?.remoteDocumentTypeId)
      ? plan?.previews.find((item) =>
          sameContentCentralId(item.externalDocumentTypeId, planType?.externalId),
        )
      : undefined;
    const committedFiling =
      !proposedFiling &&
      !changedSinceReview &&
      sameContentCentralId(selectedTypeId, mapped?.remoteDocumentTypeId)
        ? directPreview?.previews.find((item) =>
            sameContentCentralId(item.externalDocumentTypeId, selectedTypeId),
          )
        : undefined;
    return {
      remote,
      typeTag,
      fields,
      needs,
      changedSinceReview,
      filing: proposedFiling ?? committedFiling,
      choice,
      selectedTypeId,
    };
  };

  const described = localTypes.map((type) => ({ type, info: describe(type) }));
  const needCount = described.filter((item) => item.info.needs || holdReasons[item.type.id]).length;
  const current = described.find((item) => item.type.id === typeId) ?? described[0];
  const shown = described.filter((item) =>
    item.type.name.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const firstBlocker = proposal?.blockers[0] ?? plan?.blockers[0] ?? null;

  const changeType = (localTypeId: string, remoteDocumentTypeId: string) => {
    const next = choices.filter((item) => item.localDocumentTypeId !== localTypeId);
    if (remoteDocumentTypeId) {
      const prior = reviewed.get(localTypeId);
      next.push({
        localDocumentTypeId: localTypeId,
        remoteDocumentTypeId,
        fields:
          prior && sameContentCentralId(prior.remoteDocumentTypeId, remoteDocumentTypeId)
            ? { ...prior.fields }
            : {},
      });
    }
    onChoicesChange(next);
  };
  const changeField = (
    localTypeId: string,
    remoteDocumentTypeId: string,
    localFieldId: string,
    remoteFieldId: string | null,
  ) => {
    const existing = choices.find((item) => item.localDocumentTypeId === localTypeId);
    const next = choices.filter((item) => item.localDocumentTypeId !== localTypeId);
    next.push({
      localDocumentTypeId: localTypeId,
      remoteDocumentTypeId,
      fields: { ...(existing?.fields ?? {}), [localFieldId]: remoteFieldId },
    });
    onChoicesChange(next);
  };

  // A newly reported problem selects the type it is about.
  useEffect(() => {
    if (!firstBlocker) return;
    const about = localTypes.find(
      (type) => firstBlocker.startsWith(`${type.name} `) || firstBlocker.includes(`${type.name} ·`),
    );
    if (about) setTypeId(about.id);
  }, [firstBlocker, localTypes]);

  const actions =
    accepted && !inProgress ? (
      <div className="setup-save-group" role="group" aria-label="Accepted matching">
        <span className="setup-save-state">
          <span className="setup-save-dot" aria-hidden="true" />
          Accepted by Content Central
        </span>
        <button
          type="button"
          className="primary-button small"
          disabled={applying || discarding}
          onClick={onUse}
        >
          {applying ? <LoaderCircle className="spin" size={14} /> : <Check size={14} />} Use this
          matching
        </button>
      </div>
    ) : undefined;

  return (
    <WorkspaceDialog
      telemetry="content-central"
      title="Match to Content Central"
      description={`${definition.catalog.name} catalog on ${connectionName}`}
      onClose={onClose}
      actions={actions}
    >
      <div className="cc-match">
        <aside className="cc-match-types" aria-label="This job's document types">
          <div className="cc-match-counts">
            <span className="ready">
              <Check size={13} aria-hidden="true" />
              {described.length - needCount} ready
            </span>
            {needCount > 0 && (
              <span className="need">
                <TriangleAlert size={13} aria-hidden="true" />
                {needCount} need a choice
              </span>
            )}
          </div>
          <label className="cc-search">
            <Search size={15} aria-hidden="true" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Find a document type"
              aria-label="Find a document type"
            />
          </label>
          <div className="cc-match-type-list" role="listbox" aria-label="Document types">
            {shown.map(({ type, info }) => {
              const held = holdReasons[type.id];
              const attention = info.needs || Boolean(held);
              return (
                <button
                  key={type.id}
                  type="button"
                  role="option"
                  aria-selected={type.id === current?.type.id}
                  className={`cc-match-type${type.id === current?.type.id ? ' selected' : ''}`}
                  onClick={() => setTypeId(type.id)}
                >
                  <span className="cc-choice-text">
                    <strong>{type.name}</strong>
                    <small className={!info.remote || held ? 'attention' : ''}>
                      {held
                        ? 'Changed in Content Central'
                        : info.remote
                          ? `→ ${info.remote.name}${info.needs ? ' · fields need a choice' : ''}`
                          : 'Choose a Content Central type'}
                    </small>
                  </span>
                  <span
                    className={`cc-dot${attention ? ' need' : ''}`}
                    aria-label={attention ? 'Needs a choice' : 'Ready'}
                  >
                    {attention ? <TriangleAlert size={12} /> : <Check size={13} />}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        <section
          className="cc-match-center"
          aria-label={current ? `${current.type.name} matching` : 'Matching'}
        >
          {accepted && (
            <div className="cc-lockbar">
              <LockKeyhole size={15} aria-hidden="true" />
              <span>
                Content Central accepted this matching, so it’s locked until you use it. To change
                something, choose <b>Keep the current setup</b> and start again.
              </span>
            </div>
          )}
          {current && holdReasons[current.type.id] && !accepted && (
            <div className="cc-warning">
              <TriangleAlert size={15} aria-hidden="true" />
              <span className="cc-warning-text">
                <strong>Content Central changed this type.</strong> {current.type.name} documents
                wait until the matching is checked again.
                <TechnicalDetails detail={holdReasons[current.type.id]} />
              </span>
            </div>
          )}
          {current && (
            <>
              <div className="cc-match-typerow">
                <span className="cc-side">
                  <small>THIS JOB</small>
                  <strong>{current.type.name}</strong>
                </span>
                <ArrowRight size={18} aria-hidden="true" />
                <label className="cc-side">
                  <small>CONTENT CENTRAL</small>
                  <select
                    disabled={locked}
                    aria-label={`Content Central document type for ${current.type.name}`}
                    value={current.info.remote?.id ?? current.info.selectedTypeId ?? ''}
                    onChange={(event) => changeType(current.type.id, event.target.value)}
                    className={current.info.remote ? '' : 'need'}
                  >
                    <option value="">Choose a type</option>
                    {definition.documentTypes.map((type) => (
                      <option key={type.id} value={type.id} disabled={!type.canAddDocuments}>
                        {type.name}
                        {type.canAddDocuments ? '' : ' · view only'}
                      </option>
                    ))}
                  </select>
                </label>
                <FieldTagPill tag={current.info.typeTag} />
              </div>
              {!current.info.remote ? (
                <p className="cc-note cc-match-empty">
                  <Info size={15} aria-hidden="true" /> Choose which Content Central type receives{' '}
                  {current.type.name.toLowerCase()} documents. Each of your document types needs its
                  own Content Central type.
                </p>
              ) : (
                <div
                  className="cc-field-table"
                  role="table"
                  aria-label={`${current.type.name} fields`}
                >
                  <div className="cc-field-head" role="row">
                    <span role="columnheader">Our field</span>
                    <span aria-hidden="true" />
                    <span role="columnheader">Content Central field</span>
                    <span role="columnheader">Status</span>
                  </div>
                  {current.info.fields.map(({ field, selected, tag }) => {
                    const options = current.info.remote!.fields.filter(
                      (item) =>
                        !item.lineItemGroupId &&
                        item.extractable &&
                        item.valueOwner.toLowerCase() === 'capturepoint',
                    );
                    return (
                      <div
                        key={field.id}
                        role="row"
                        className={`cc-field-row${tag.tone === 'need' ? ' need' : ''}`}
                      >
                        <span role="cell" className="cc-field-ours">
                          <strong>{field.name}</strong>
                          {field.required && <small>Required</small>}
                        </span>
                        <ArrowRight size={14} aria-hidden="true" />
                        <span role="cell">
                          <select
                            disabled={locked}
                            aria-label={`Content Central field for ${field.name}`}
                            value={selected}
                            onChange={(event) =>
                              changeField(
                                current.type.id,
                                current.info.remote!.id,
                                field.id,
                                event.target.value || null,
                              )
                            }
                          >
                            <option value="__unmapped__" disabled>
                              Choose a field
                            </option>
                            <option value="">Do not send</option>
                            {options.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.name} · {item.valueType}
                                {item.required ? ' · required' : ''}
                              </option>
                            ))}
                          </select>
                        </span>
                        <span role="cell">
                          <FieldTagPill tag={tag} />
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </section>

        <aside className="cc-match-verdict" aria-live="polite">
          {inProgress ? (
            <ProvisioningProgress
              plan={plan!}
              applyState={applyState}
              outcomeKnown={outcomeKnown}
              errorMessage={applyError}
              applying={applying}
              discarding={discarding}
              onApply={onUse}
              onKeep={onKeep}
            />
          ) : (
            <>
              {reviewing ? (
                <div className="cc-verdict busy">
                  <LoaderCircle className="spin" size={16} aria-hidden="true" />
                  <span>
                    <strong>Checking with Content Central…</strong>This can take a little while.
                  </span>
                </div>
              ) : checkError ? (
                <div
                  className="cc-verdict bad"
                  role="alert"
                  data-telemetry="error:catalog-matching-failed"
                >
                  <TriangleAlert size={16} aria-hidden="true" />
                  <span>
                    <strong>Content Central couldn’t check this matching</strong>
                    <OperatorErrorText message={checkError} />
                  </span>
                </div>
              ) : dirty ? (
                <div className="cc-verdict todo">
                  <Clock3 size={16} aria-hidden="true" />
                  <span>
                    <strong>Changes not checked yet</strong>Content Central checks the whole
                    matching before it can be used.
                  </span>
                </div>
              ) : firstBlocker === REVIEW_BEFORE_RESUMING ? (
                <div className="cc-verdict todo">
                  <Clock3 size={16} aria-hidden="true" />
                  <span>
                    <strong>Check this matching again</strong>The catalog changed since it was
                    saved. Content Central checks it before export resumes.
                  </span>
                </div>
              ) : firstBlocker ? (
                <div className="cc-verdict bad">
                  <TriangleAlert size={16} aria-hidden="true" />
                  <span>
                    <strong>Content Central found something to fix</strong>
                    <OperatorErrorText message={firstBlocker} />
                  </span>
                </div>
              ) : accepted ? (
                <div className="cc-verdict ok">
                  <CheckCircle2 size={16} aria-hidden="true" />
                  <span>
                    <strong>Content Central accepts this matching</strong>Nothing changes in Content
                    Central. Its existing filing stays as it is.
                  </span>
                </div>
              ) : (
                <div className="cc-verdict ok">
                  <CheckCircle2 size={16} aria-hidden="true" />
                  <span>
                    <strong>This matching is in use</strong>Change a choice, then check it with
                    Content Central.
                  </span>
                </div>
              )}
              {!accepted && (
                <button
                  type="button"
                  className="primary-button cc-wide"
                  disabled={
                    locked || reviewing || choices.length === 0 || (!dirty && !firstBlocker)
                  }
                  data-telemetry="content-central-check-matching"
                  onClick={onCheck}
                >
                  <RotateCw size={15} /> Check with Content Central
                </button>
              )}
              {accepted && (
                <button
                  type="button"
                  className="secondary-button cc-wide"
                  disabled={applying || discarding}
                  onClick={onKeep}
                >
                  {discarding && <LoaderCircle className="spin" size={14} />}Keep the current setup
                </button>
              )}
            </>
          )}
          {current?.info.remote && (
            <div className="cc-example">
              <strong>Example built by Content Central</strong>
              {current.info.filing ? (
                <>
                  <div className="cc-example-box">
                    <small>
                      <Database size={13} aria-hidden="true" /> In the {definition.catalog.name}{' '}
                      catalog
                    </small>
                    <FilingPath
                      folders={current.info.filing.logicalFolders}
                      fileName={current.info.filing.filename}
                    />
                  </div>
                  {current.info.filing.diagnostics.map((item) => (
                    <small key={item} className="cc-note attention">
                      {item}
                    </small>
                  ))}
                  <small className="cc-note">
                    Content Central builds this from its own filing for “{current.info.remote.name}
                    ”, using sample values. To change folders or names, edit the catalog’s filing in
                    Content Central.
                  </small>
                </>
              ) : (
                <small className="cc-note">
                  {dirty
                    ? 'Check this matching to see the example Content Central builds.'
                    : 'Content Central shows an example once this type is matched and checked.'}
                </small>
              )}
            </div>
          )}
        </aside>
      </div>
    </WorkspaceDialog>
  );
}

function FieldTagPill({ tag }: { tag: FieldTag }) {
  return <span className={`cc-tag ${tag.tone}`}>{tag.text}</span>;
}

/** The accepted operation's stages, shared by matching and creation. */
function ProvisioningProgress({
  plan,
  applyState,
  outcomeKnown,
  errorMessage,
  applying,
  discarding,
  onApply,
  onKeep,
}: {
  plan: ContentCentralProvisioningPlan;
  applyState: string | null;
  outcomeKnown: boolean;
  errorMessage: string | null;
  applying: boolean;
  discarding: boolean;
  onApply: () => void;
  onKeep: () => void;
}) {
  const resuming = applyState === 'running';
  const dispatchNotConfirmed = applyState === 'starting';
  const reconciliationNeeded = applyState === 'reconciliation-needed';
  const remoteCompleted = applyState === 'completed' || reconciliationNeeded;
  const needsAttention =
    applyState === 'needs-attention' || reconciliationNeeded || dispatchNotConfirmed;
  const stages = ['Plan submitted', 'Content Central working', 'Verification'];
  const title = resuming
    ? 'Content Central is building this setup'
    : dispatchNotConfirmed
      ? 'Continue the saved Content Central setup'
      : remoteCompleted
        ? 'Connect the completed Content Central setup'
        : needsAttention
          ? 'Content Central needs attention'
          : 'Content Central is building this setup';
  const text = resuming
    ? 'The accepted operation is saved. Continue checking the same operation; no duplicate catalog will be created.'
    : dispatchNotConfirmed
      ? "Paige saved this exact setup but hasn't confirmed that Content Central started it. Continue to safely resume the same operation."
      : reconciliationNeeded
        ? "Content Central finished the remote setup, but Paige couldn't safely connect its fields. Continue to retry the exact result, or leave that remote setup unconnected; the record is kept."
        : remoteCompleted
          ? 'Content Central finished the remote setup. Continue to check it and connect it to this job.'
          : needsAttention
            ? "The accepted operation is saved, but Content Central couldn't finish it. Check Content Central before preparing another setup."
            : '';
  return (
    <div className="cc-progress" role="status" aria-live="polite">
      <strong className="cc-progress-title">{title}</strong>
      <small>{text}</small>
      <ol className="cc-progress-stages" aria-label="Content Central setup progress">
        {stages.map((stage, index) => {
          const complete = index === 0 || (remoteCompleted && index === 1);
          const attention =
            (needsAttention && !remoteCompleted && index === 1) ||
            (reconciliationNeeded && index === 2);
          const active = resuming && index === 1;
          const ready = applyState === 'completed' && index === 2;
          return (
            <li
              key={stage}
              className={attention ? 'attention' : complete ? 'done' : active ? 'current' : ''}
            >
              <span>
                {complete ? (
                  <Check size={12} />
                ) : attention ? (
                  <TriangleAlert size={12} />
                ) : active ? (
                  <LoaderCircle className="spin" size={12} />
                ) : (
                  <Clock3 size={12} />
                )}
              </span>
              <strong>{stage}</strong>
              <small>
                {complete
                  ? index === 0
                    ? 'Accepted'
                    : 'Completed'
                  : attention
                    ? reconciliationNeeded && index === 2
                      ? 'Action required'
                      : 'Needs attention'
                    : ready
                      ? 'Ready to continue'
                      : active
                        ? 'In progress'
                        : 'Pending'}
              </small>
            </li>
          );
        })}
      </ol>
      {needsAttention && errorMessage && (
        <div className="cc-inline-error" role="alert" data-telemetry="error:catalog-setup-failed">
          <TriangleAlert size={13} aria-hidden="true" />
          <OperatorErrorText message={errorMessage} />
        </div>
      )}
      <div className="cc-progress-actions">
        <button
          type="button"
          className="primary-button"
          disabled={applying || discarding || plan.blockers.length > 0}
          onClick={onApply}
        >
          {applying ? <LoaderCircle className="spin" size={14} /> : <RotateCw size={14} />}{' '}
          {applying ? 'Checking setup…' : 'Continue setup'}
        </button>
        {applyState !== 'completed' &&
          (reconciliationNeeded || (needsAttention && outcomeKnown)) && (
            <button
              type="button"
              className="quiet-button"
              disabled={applying || discarding}
              onClick={onKeep}
            >
              {discarding && <LoaderCircle className="spin" size={13} />}
              {reconciliationNeeded ? 'Leave remote setup unconnected' : 'Dismiss failed setup'}
            </button>
          )}
      </div>
      <small className="cc-note">
        You can close this window. The setup is saved; reopen Export to continue the same operation.
        No second catalog is created.
      </small>
    </div>
  );
}

type ProvisioningChange = ContentCentralProvisioningPlan['changes'][number];
function provisioningSections(plan: ContentCentralProvisioningPlan) {
  const result: Array<{
    type: ProvisioningChange;
    fields: ProvisioningChange[];
    filing?: ProvisioningChange;
  }> = [];
  let current: {
    type: ProvisioningChange;
    fields: ProvisioningChange[];
    filing?: ProvisioningChange;
  } | null = null;
  for (const change of plan.changes) {
    if (change.objectKind === 'document-type') {
      current = { type: change, fields: [] };
      result.push(current);
    } else if (change.objectKind === 'field' && current) current.fields.push(change);
    else if (change.objectKind === 'filing-rule' && current) current.filing = change;
  }
  return result;
}

function fieldKindLabel(details?: Record<string, unknown> | null): string {
  if (!details || typeof details.valueType !== 'string') return 'field';
  return details.valueType === 'decimal' ? 'amount' : details.valueType;
}

/**
 * Create a catalog from this job: what Content Central will be asked to build,
 * then the real stages of that operation, then the result.
 */
export function ContentCentralCreateWindow({
  jobName,
  connectionName,
  plan,
  applyState,
  outcomeKnown,
  errorMessage,
  applying,
  discarding,
  completed,
  exportOn,
  onCreate,
  onKeep,
  onClose,
}: {
  jobName: string;
  connectionName: string;
  plan: ContentCentralProvisioningPlan | null;
  applyState: string | null;
  outcomeKnown: boolean;
  errorMessage: string | null;
  applying: boolean;
  discarding: boolean;
  completed: { typeCount: number; fieldCount: number; reuse: boolean } | null;
  /** Whether this job's export is already switched on, so the new setup is used at once. */
  exportOn: boolean;
  onCreate: () => void;
  onKeep: () => void;
  onClose: () => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const [openType, setOpenType] = useState<string | null>(null);
  // The same window shows a saved plan to use an existing catalog: it needs
  // only the plan, so it also reopens a setup saved before a restart.
  const reuse = completed
    ? completed.reuse
    : plan?.changes.find((change) => change.objectKind === 'catalog')?.action === 'reuse';
  const title = reuse ? 'Use a Content Central catalog' : 'Create a Content Central catalog';
  // Only a finished Apply is a connected setup; a missing plan is not success.
  if (!completed && !plan) return null;
  if (completed || !plan) {
    return (
      <WorkspaceDialog
        telemetry="content-central-create"
        title={title}
        description={connectionName}
        onClose={onClose}
      >
        <div className="cc-create-done" role="status">
          <span className="cc-done-mark">
            <Check size={26} aria-hidden="true" />
          </span>
          <h3>Content Central setup connected</h3>
          <p>
            “{jobName}” has {countOf(completed?.typeCount ?? 0, 'document type')},{' '}
            {countOf(completed?.fieldCount ?? 0, 'field')} and their filing.{' '}
            {exportOn ? 'Documents now go there.' : 'Save Job setup to start exporting there.'}
          </p>
          <button type="button" className="primary-button" onClick={onClose}>
            Back to Export
          </button>
        </div>
      </WorkspaceDialog>
    );
  }
  const catalog = plan.changes.find((change) => change.objectKind === 'catalog');
  const permission = plan.changes.find((change) => change.objectKind === 'permission');
  const sections = provisioningSections(plan);
  const fieldCount = plan.changes.filter((change) => change.objectKind === 'field').length;
  const filingCount = sections.filter((section) => section.filing).length;
  const shownSections = showAll ? sections : sections.slice(0, 4);
  const accepted = Boolean(applyState);
  return (
    <WorkspaceDialog
      telemetry="content-central-create"
      title={title}
      description={connectionName}
      onClose={onClose}
    >
      {accepted ? (
        <div className="cc-create-progress">
          <ProvisioningProgress
            plan={plan}
            applyState={applyState}
            outcomeKnown={outcomeKnown}
            errorMessage={errorMessage}
            applying={applying}
            discarding={discarding}
            onApply={onCreate}
            onKeep={onKeep}
          />
        </div>
      ) : (
        <div className="cc-create-window">
          <section className="cc-create-main">
            {reuse ? (
              <div className="cc-create-name">
                <small>Catalog</small>
                <h3>{catalog?.displayName ?? 'Selected catalog'}</h3>
                <small>
                  Already in Content Central. Nothing there changes until you use this matching.
                </small>
              </div>
            ) : (
              <div className="cc-create-name">
                <small>New catalog</small>
                <h3>{catalog?.displayName ?? jobName}</h3>
                <small>Named after this job. Rename the job to change it.</small>
              </div>
            )}
            <div className="cc-create-counts">
              <span>
                <b>1</b>
                {reuse ? 'catalog' : 'new catalog'}
              </span>
              <span>
                <b>{sections.length}</b>document types
              </span>
              <span>
                <b>{fieldCount}</b>fields
              </span>
              <span>
                <b>{filingCount}</b>filing setups
              </span>
            </div>
            <div className="cc-create-types">
              {shownSections.map((section) => {
                const open = openType === section.type.externalId;
                return (
                  <div
                    key={section.type.externalId}
                    className={`cc-create-type${open ? ' open' : ''}`}
                  >
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() => setOpenType(open ? null : section.type.externalId)}
                    >
                      <FileText size={15} aria-hidden="true" />
                      <strong>{section.type.displayName}</strong>
                      <small>{section.fields.length} fields</small>
                      <ChevronDown size={14} aria-hidden="true" />
                    </button>
                    {open && (
                      <div className="cc-create-fields">
                        {section.fields.map((field) => (
                          <span key={field.externalId}>
                            {field.displayName}
                            <small>
                              {fieldKindLabel(field.details)}
                              {field.details?.required === true ? ' · required' : ''}
                            </small>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {sections.length > 4 && (
                <button
                  type="button"
                  className="quiet-button small"
                  onClick={() => setShowAll((value) => !value)}
                >
                  <ChevronDown size={13} />{' '}
                  {showAll ? 'Show fewer' : `Show the other ${sections.length - 4} document types`}
                </button>
              )}
            </div>
            {permission && (
              <p className="cc-note cc-create-permission">
                <ShieldCheck size={15} aria-hidden="true" />
                {connectionName} will be allowed to read this catalog, add documents, and manage
                these document types, fields and filing.
              </p>
            )}
          </section>
          <aside className="cc-create-side">
            <strong>Examples built by Content Central</strong>
            <small className="cc-note">
              {reuse
                ? 'Content Central builds these from the catalog’s own filing, with sample values. Its filing doesn’t change.'
                : 'Your current folders and file names become the catalog’s filing. Content Central shows them with sample values.'}
            </small>
            <div className="cc-create-examples">
              {plan.previews.slice(0, 3).map((preview, index) => {
                const type = sections.find((section) =>
                  sameContentCentralId(section.type.externalId, preview.externalDocumentTypeId),
                );
                return (
                  <div key={preview.externalDocumentTypeId} className="cc-example-box">
                    <small>{type?.type.displayName ?? `Document type ${index + 1}`}</small>
                    <FilingPath folders={preview.logicalFolders} fileName={preview.filename} />
                    {preview.diagnostics.map((item) => (
                      <small key={item} className="cc-note attention">
                        {item}
                      </small>
                    ))}
                  </div>
                );
              })}
            </div>
            {plan.warnings.map((warning) => (
              <p key={warning} className="cc-warning small">
                <TriangleAlert size={14} aria-hidden="true" />
                <span className="cc-warning-text">{warning}</span>
              </p>
            ))}
            {plan.blockers.map((blocker) => (
              <div
                key={blocker}
                className="cc-inline-error"
                role="alert"
                data-telemetry="error:catalog-plan-blocked"
              >
                <TriangleAlert size={13} aria-hidden="true" />
                <OperatorErrorText message={blocker} />
              </div>
            ))}
            <div className="cc-create-actions">
              <small className="cc-note">
                {reuse
                  ? 'Content Central checks this matching again when you use it.'
                  : 'Nothing is created in Content Central until you click Create catalog.'}
              </small>
              <button
                type="button"
                className="primary-button cc-wide"
                disabled={applying || discarding || plan.blockers.length > 0}
                onClick={onCreate}
              >
                {applying ? (
                  <LoaderCircle className="spin" size={15} />
                ) : reuse ? (
                  <Check size={15} />
                ) : (
                  <Sparkles size={15} />
                )}{' '}
                {applying ? 'Checking setup…' : reuse ? 'Use this matching' : 'Create catalog'}
              </button>
              <button
                type="button"
                className="quiet-button small"
                disabled={applying || discarding}
                onClick={onKeep}
              >
                {discarding ? 'Updating setup…' : 'Keep the current setup'}
              </button>
            </div>
          </aside>
        </div>
      )}
    </WorkspaceDialog>
  );
}
